import React, { useEffect } from "react";

// import Scss
import "./assets/scss/themes.scss";
import "./App.css";

// import Route
import Route from "./Routes";

// Fake Backend
import fakeBackend from "./helpers/AuthType/fakeBackend";
import { useDispatch, useSelector } from "react-redux";
import { loginSuccess, renewToken } from "./store/auth/login/actions";
import { isEmpty } from "lodash";

import { onKeyPressedDownSetKeySuccess } from "./store/onKeyPressedDown/action";
import {
  isTokenExpired,
  clearStoredAuth,
  getTokenCompanyId,
} from "./helpers/authToken";
import FeedbackGenTrigger from "./Components/Common/feedbackGenTrigger";
import GoogleFirebase from "./Components/GoogleFireBase";
import Branding from "./Components/branding/Branding";
// Task 24498 - listen for Google rejecting the Maps key, so the failure is
// said out loud instead of appearing as a watermarked map on one screen.
import { registerGoogleMapsAuthFailureHandler } from "./helpers/functions/googleMapsAuth";

import {
  TwilioCallProvider,
  TwilioIncomingCallPopup,
} from "./Components/Twilio";

// Suppress ResizeObserver loop error
const resizeObserverLoopErrMin = "ResizeObserver loop limit exceeded";
const resizeObserverLoopErr =
  "ResizeObserver loop completed with undelivered notifications.";

const handler = (e) => {
  if (
    e.message === resizeObserverLoopErr ||
    e.message === resizeObserverLoopErrMin
  ) {
    const resizeObserverErrDiv = document.getElementById(
      "webpack-dev-server-client-overlay-div"
    );
    const resizeObserverErr = document.getElementById(
      "webpack-dev-server-client-overlay"
    );

    if (resizeObserverErr) {
      resizeObserverErr.setAttribute("style", "display: none");
    }

    if (resizeObserverErrDiv) {
      resizeObserverErrDiv.setAttribute("style", "display: none");
    }
  }
};

window.addEventListener("error", handler);
window.addEventListener("unhandledrejection", handler);

fakeBackend();

// Remember-me enforcement (runs once at module load, before React renders).
// If the last login was session-only (authPersist === "0") and this is a fresh
// browser session (the per-session "authSession" sentinel is gone because the
// browser was closed), clear the stored auth so the user must sign in again.
// Remembered logins (authPersist "1" or absent, e.g. legacy sessions) are untouched.
(function enforceRememberMe() {
  try {
    const persist = localStorage.getItem("authPersist");
    if (persist === "0" && !sessionStorage.getItem("authSession")) {
      localStorage.removeItem("authUser");
      localStorage.removeItem("authPersist");
    }
  } catch (_) {
    // ignore storage access errors
  }
})();

const App = () => {
  const dispatch = useDispatch();

  const { loggedOutUser, loggedInUser } = useSelector((state) => ({
    loggedOutUser: state.Login.isUserLogout,
    loggedInUser: state.Login.isUserLogin,
  }));
  // Restore login from localStorage on first loadcolives on but check and analyse like waht other bicg techhow other comapnies solve this and how we can amkea
  useEffect(() => {
    const stored = localStorage.getItem("authUser");
    if (!stored) return;

    try {
      const parsed = JSON.parse(stored);

      // Do not restore an expired session. Treating a dead token as "logged in"
      // is what let the authorised UI flash before the redirect to /login. If
      // the token is expired, clear the stale session and let the router fall
      // through to /login instead of dispatching loginSuccess.
      if (parsed && isTokenExpired(parsed.token)) {
        clearStoredAuth();
        return;
      }

      // The token decides which company the backend serves; get_company_data
      // decides which company the UI claims to be in. They are written together
      // on login and on a company switch, so if they ever disagree the session
      // is showing one company's branding over another company's data. Surface
      // it loudly rather than letting it look like a backend fault.
      const var___token_company_id = getTokenCompanyId(parsed?.token);
      const var___session_company_id =
        parsed?.get_company_data?.Company_IdExternal;
      if (
        var___token_company_id &&
        var___session_company_id &&
        var___token_company_id !== var___session_company_id
      ) {
        console.warn(
          "authUser is split across companies - the token addresses",
          var___token_company_id,
          "but the session is displaying",
          var___session_company_id,
          "- switch company again to resynchronise"
        );
      }

      if (!isEmpty(parsed)) {
        dispatch(loginSuccess(parsed));
      }
    } catch (err) {
      console.error("Failed to parse authUser from localStorage", err);
    }
  }, [dispatch]);

  // Stop the mouse wheel from changing the value of a focused number input.
  // <input type="number"> natively treats wheel scroll as increment/decrement
  // when focused, which silently corrupts quantities/prices inside scrollable
  // modals. Blurring on wheel drops focus so the value stays put and the page
  // scrolls normally. Registered once globally so it applies to every number
  // field in the app.
  // Task 24498 - registered once, before any map mounts.
  useEffect(() => {
    registerGoogleMapsAuthFailureHandler();
  }, []);

  useEffect(() => {
    const handleWheel = (e) => {
      const el = document.activeElement;
      if (el && el === e.target && el.tagName === "INPUT" && el.type === "number") {
        el.blur();
      }
    };
    document.addEventListener("wheel", handleWheel);
    return () => document.removeEventListener("wheel", handleWheel);
  }, []);

  // Immediately renew token when user becomes logged in
  useEffect(() => {
    if (!loggedInUser) return;

    const timer = setTimeout(() => {
      dispatch(renewToken());
    }, 30000);

    return () => clearTimeout(timer);
  }, [loggedInUser, dispatch]);

  // Renew token on a fixed interval while logged in
  useEffect(() => {
    if (!loggedInUser) return;

    const TokenEngineOn = setInterval(() => {
      dispatch(renewToken());
    }, 30 * 60 * 1000);

    return () => {
      clearInterval(TokenEngineOn);
    };
  }, [loggedInUser, dispatch]);

  // After browser/tab becomes visible or window gains focus, renew token
  useEffect(() => {
    if (!loggedInUser) return;

    // Both events fire far more often than a renewal is worth - alt-tabbing,
    // clicking back in from DevTools, and a visibilitychange plus a focus for
    // the same return to the tab. Each renewal is a heavy round trip that ends
    // by rewriting the stored session, so throttle them to one a minute.
    const RENEW_MIN_INTERVAL_MS = 60 * 1000;
    let var___last_renew_at = 0;

    const requestRenew = () => {
      const now = Date.now();
      if (now - var___last_renew_at < RENEW_MIN_INTERVAL_MS) return;
      var___last_renew_at = now;
      dispatch(renewToken());
    };

    const handleVisibilityChange = () => {
      if (document.visibilityState === "visible") {
        requestRenew();
      }
    };

    const handleFocus = () => {
      requestRenew();
    };

    document.addEventListener("visibilitychange", handleVisibilityChange);
    window.addEventListener("focus", handleFocus);

    return () => {
      document.removeEventListener("visibilitychange", handleVisibilityChange);
      window.removeEventListener("focus", handleFocus);
    };
  }, [loggedInUser, dispatch]);

  const authUser = localStorage.getItem("authUser");

  return (
    <React.Fragment>
      <TwilioCallProvider>
        <div
          onKeyDown={(e) => {
            if (!loggedInUser) return;
            dispatch(onKeyPressedDownSetKeySuccess(e.key));
          }}
        >
          <Branding />

          {authUser && <GoogleFirebase getToken={true} />}

          <Route />

          {authUser && <TwilioIncomingCallPopup />}

          <FeedbackGenTrigger />
        </div>
      </TwilioCallProvider>
    </React.Fragment>
  );
};

export default App;
